import { useState } from 'react';
import { Link } from 'react-router-dom';
import type {
  AdminWorldDetail,
  AdminWorldState,
  AdminWorldEvolution,
  AdminWorldVisualGeneration,
} from '../../../lib/api';
import { JsonViewer } from './JsonViewer';

type HistoryTab = 'activity' | 'evolutions' | 'diffs' | 'summaries' | 'archives' | 'visuals';

interface WorldHistoryProps {
  world: AdminWorldDetail;
  state: AdminWorldState | null;
  evolutions: AdminWorldEvolution[];
  visuals: AdminWorldVisualGeneration[];
}

export function WorldHistory({ world, state, evolutions, visuals }: WorldHistoryProps) {
  const [activeTab, setActiveTab] = useState<HistoryTab>('activity');

  const tabs: Array<{ id: HistoryTab; label: string; count?: number }> = [
    { id: 'activity', label: 'Activity' },
    { id: 'evolutions', label: 'Evolutions', count: evolutions.length },
    { id: 'diffs', label: 'Diffs', count: state?.diffs?.length ?? 0 },
    { id: 'summaries', label: 'Summaries' },
    { id: 'archives', label: 'Archives' },
    { id: 'visuals', label: 'Visuals', count: visuals.length },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="font-display text-xl text-parchment">History</h1>
        <p className="text-mist text-sm mt-1">Activity, evolutions, and world state changes</p>
      </div>

      {/* Tabs */}
      <div className="bg-night border border-stone/30 rounded-xl overflow-hidden">
        <div className="flex border-b border-stone/30 overflow-x-auto">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-3 text-sm transition-colors whitespace-nowrap flex items-center gap-2 ${
                activeTab === tab.id
                  ? 'bg-rune/20 text-rune border-b-2 border-rune'
                  : 'text-mist hover:text-parchment hover:bg-stone/20'
              }`}
            >
              {tab.label}
              {tab.count !== undefined && (
                <span className="text-xs opacity-60">({tab.count})</span>
              )}
            </button>
          ))}
        </div>

        <div className="p-4 sm:p-6">
          {activeTab === 'activity' && <ActivityView world={world} />}
          {activeTab === 'evolutions' && <EvolutionsView evolutions={evolutions} />}
          {activeTab === 'diffs' && <DiffsView diffs={state?.diffs ?? []} />}
          {activeTab === 'summaries' && <SummariesView summaries={state?.summaries} />}
          {activeTab === 'archives' && <ArchivesView archives={state?.archives} />}
          {activeTab === 'visuals' && <VisualsView visuals={visuals} />}
        </div>
      </div>
    </div>
  );
}

function VisualsView({ visuals }: { visuals: AdminWorldVisualGeneration[] }) {
  if (visuals.length === 0) {
    return (
      <div className="text-mist text-sm text-center py-8">
        No world visuals generated yet.
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {visuals.map((visual) => (
        <div key={visual.id} className="border border-stone/30 rounded-lg p-4">
          <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
            <div className="text-sm text-mist">
              {formatDate(visual.createdAt)}
              {visual.worldStateVersion !== null ? ` · v${visual.worldStateVersion}` : ''}
            </div>
            <div className="text-xs text-mist/70">
              {visual.model ?? 'model unknown'} · {visual.status}
            </div>
          </div>

          {visual.imageUrl ? (
            <div className="mt-3">
              <img
                src={visual.imageUrl}
                alt="World visual"
                className="w-full max-w-xl rounded-lg border border-stone/20"
              />
            </div>
          ) : (
            <div className="text-mist/60 text-sm mt-3">No image URL recorded.</div>
          )}

          {visual.prompt && (
            <details className="cursor-pointer mt-3">
              <summary className="text-sm text-mist hover:text-parchment">
                View Prompt
              </summary>
              <div className="mt-2 p-3 bg-void rounded-lg text-xs text-mist overflow-auto max-h-40">
                {visual.prompt}
              </div>
            </details>
          )}

          {visual.errorMessage && (
            <div className="mt-3 text-xs text-ember">Error: {visual.errorMessage}</div>
          )}
        </div>
      ))}
    </div>
  );
}

function ActivityView({ world }: { world: AdminWorldDetail }) {
  return (
    <div className="space-y-6">
      {/* Recent Inbound */}
      <div>
        <h3 className="font-display text-parchment mb-3">Recent Inbound Letters</h3>
        {world.recentLettersIn.length === 0 ? (
          <div className="text-mist text-sm py-4">No letters received</div>
        ) : (
          <div className="space-y-2">
            {world.recentLettersIn.map((letter) => (
              <Link
                key={letter.id}
                to={`/admin/letters-in/${letter.id}`}
                className="flex items-center justify-between border border-stone/20 rounded-lg p-3 hover:bg-stone/10 transition-colors"
              >
                <div className="flex-1 min-w-0">
                  <div className="text-parchment text-sm truncate">
                    {letter.subject ?? '(No subject)'}
                  </div>
                  <div className="text-mist text-xs">From: {letter.fromEmail}</div>
                </div>
                <div className="text-right ml-4">
                  <StatusBadge status={letter.status} />
                  <div className="text-mist/50 text-xs mt-1">{formatDate(letter.receivedAt)}</div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>

      {/* Recent Outbound */}
      <div>
        <h3 className="font-display text-parchment mb-3">Recent Outbound Letters</h3>
        {world.recentLettersOut.length === 0 ? (
          <div className="text-mist text-sm py-4">No letters sent</div>
        ) : (
          <div className="space-y-2">
            {world.recentLettersOut.map((letter) => (
              <Link
                key={letter.id}
                to={`/admin/letters-out/${letter.id}`}
                className="flex items-center justify-between border border-stone/20 rounded-lg p-3 hover:bg-stone/10 transition-colors"
              >
                <div className="flex-1 min-w-0">
                  <div className="text-parchment text-sm truncate">{letter.subject}</div>
                  <div className="text-mist text-xs">Type: {letter.type}</div>
                </div>
                <div className="text-right ml-4">
                  <StatusBadge status={letter.status} />
                  <div className="text-mist/50 text-xs mt-1">
                    {letter.sentAt ? formatDate(letter.sentAt) : formatDate(letter.createdAt)}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function EvolutionsView({ evolutions }: { evolutions: AdminWorldEvolution[] }) {
  if (evolutions.length === 0) {
    return (
      <div className="text-mist text-sm text-center py-8">
        No evolutions recorded yet. Use "Evolve World" or wait for new letters.
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {evolutions.map((evolution) => {
        const stats = getMergeStats(evolution.mergeReport);
        const llmMeta = evolution.llm && typeof evolution.llm === 'object'
          ? (evolution.llm as Record<string, unknown>)
          : null;
        const delta = llmMeta?.delta;

        return (
          <div key={evolution.id} className="border border-stone/30 rounded-lg p-4">
            <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
              <div className="text-sm text-mist">
                {formatDate(evolution.createdAt)} · v{evolution.worldStateVersion}
              </div>
              {evolution.letterOut ? (
                <Link
                  to={`/admin/letters-out/${evolution.letterOut.id}`}
                  className="text-rune hover:underline text-xs"
                >
                  Outbound: {evolution.letterOut.subject}
                </Link>
              ) : (
                <span className="text-xs text-mist/60">Manual or inbound-triggered</span>
              )}
            </div>

            {stats && (
              <div className="mt-2 text-xs text-mist/70">
                Entities +{stats.entities_created} · Updates {stats.entities_updated} · Events {stats.events_added}
                <span className="ml-3">Threads {stats.threads_touched}</span>
                <span className="ml-3">Constraints {stats.constraints_added}</span>
              </div>
            )}

            <details className="cursor-pointer mt-3">
              <summary className="text-sm text-mist hover:text-parchment">View Merge Report</summary>
              <pre className="mt-2 p-3 bg-void rounded-lg text-xs text-mist overflow-auto max-h-64">
                {JSON.stringify(evolution.mergeReport, null, 2)}
              </pre>
            </details>

            {getMergeChanges(evolution.mergeReport).length > 0 && (
              <details className="cursor-pointer mt-2">
                <summary className="text-sm text-mist hover:text-parchment">
                  Change Summary ({getMergeChanges(evolution.mergeReport).length})
                </summary>
                <div className="mt-2 space-y-2 text-xs text-mist">
                  {getMergeChanges(evolution.mergeReport).map((change, index) => (
                    <div key={`${evolution.id}-change-${index}`} className="p-2 bg-void rounded">
                      <div className="text-parchment">
                        {formatChangeLabel(change)} - {change.summary ?? 'No summary'}
                      </div>
                      {change.target && (
                        <div className="text-mist/70 mt-1">
                          Target: {change.target.type ?? 'unknown'} {change.target.id ?? ''}
                          {change.target.field ? `.${change.target.field}` : ''}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </details>
            )}

            {delta !== undefined && (
              <details className="cursor-pointer mt-2">
                <summary className="text-sm text-mist hover:text-parchment">View World Builder Delta</summary>
                <pre className="mt-2 p-3 bg-void rounded-lg text-xs text-mist overflow-auto max-h-64">
                  {JSON.stringify(delta, null, 2)}
                </pre>
              </details>
            )}
          </div>
        );
      })}
    </div>
  );
}

function DiffsView({ diffs }: { diffs: Array<{ id: string; letterInId: string | null; mergeReport: unknown; createdAt: string }> }) {
  if (diffs.length === 0) {
    return <div className="text-mist text-sm text-center py-8">No diffs for this version</div>;
  }

  return (
    <div className="space-y-4">
      {diffs.map((diff) => (
        <div key={diff.id} className="border border-stone/30 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <div className="text-sm">
              <span className="text-mist">Letter: </span>
              {diff.letterInId ? (
                <Link
                  to={`/admin/letters-in/${diff.letterInId}`}
                  className="text-rune hover:underline font-mono"
                >
                  {diff.letterInId}
                </Link>
              ) : (
                <span className="text-mist/50">System</span>
              )}
            </div>
            <div className="text-xs text-mist">{formatDate(diff.createdAt)}</div>
          </div>

          {getMergeChanges(diff.mergeReport).length > 0 && (
            <details className="cursor-pointer mb-2">
              <summary className="text-sm text-mist hover:text-parchment">
                Change Summary ({getMergeChanges(diff.mergeReport).length})
              </summary>
              <div className="mt-2 space-y-2 text-xs text-mist">
                {getMergeChanges(diff.mergeReport).map((change, index) => (
                  <div key={`${diff.id}-change-${index}`} className="p-2 bg-void rounded">
                    <div className="text-parchment">
                      {formatChangeLabel(change)} - {change.summary ?? 'No summary'}
                    </div>
                    {change.target && (
                      <div className="text-mist/70 mt-1">
                        Target: {change.target.type ?? 'unknown'} {change.target.id ?? ''}
                        {change.target.field ? `.${change.target.field}` : ''}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </details>
          )}

          <details className="cursor-pointer">
            <summary className="text-sm text-mist hover:text-parchment">View Merge Report</summary>
            <div className="mt-2 p-3 bg-void rounded-lg text-xs font-mono overflow-auto max-h-64">
              <JsonViewer data={diff.mergeReport} />
            </div>
          </details>
        </div>
      ))}
    </div>
  );
}

function SummariesView({ summaries }: { summaries: unknown }) {
  if (!summaries) {
    return <div className="text-mist text-sm text-center py-8">No summaries available</div>;
  }

  return (
    <div className="bg-void rounded-lg p-4 text-sm font-mono overflow-auto max-h-[600px]">
      <JsonViewer data={summaries} />
    </div>
  );
}

function ArchivesView({ archives }: { archives: unknown }) {
  if (!archives) {
    return <div className="text-mist text-sm text-center py-8">No archives available</div>;
  }

  return (
    <div className="bg-void rounded-lg p-4 text-sm font-mono overflow-auto max-h-[600px]">
      <JsonViewer data={archives} />
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const colors: Record<string, string> = {
    processed: 'bg-success/20 text-success',
    sent: 'bg-success/20 text-success',
    rejected: 'bg-ember/20 text-ember',
    failed: 'bg-ember/20 text-ember',
    pending: 'bg-stone/30 text-mist',
    queued: 'bg-stone/30 text-mist',
  };

  return (
    <span className={`px-2 py-0.5 rounded text-xs ${colors[status] ?? 'bg-stone/30 text-mist'}`}>
      {status}
    </span>
  );
}

function formatDate(isoString: string): string {
  const date = new Date(isoString);
  return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
}

type MergeChange = {
  category?: string;
  action?: string;
  summary?: string;
  target?: {
    id?: string;
    type?: string;
    field?: string;
  };
};

function getMergeStats(report: unknown): {
  entities_created: number;
  entities_updated: number;
  events_added: number;
  threads_touched: number;
  constraints_added: number;
} | null {
  if (!report || typeof report !== 'object') return null;
  const stats = (report as Record<string, unknown>).stats;
  if (!stats || typeof stats !== 'object') return null;
  const value = (key: string) => {
    const raw = (stats as Record<string, unknown>)[key];
    return typeof raw === 'number' ? raw : 0;
  };
  return {
    entities_created: value('entities_created'),
    entities_updated: value('entities_updated'),
    events_added: value('events_added'),
    threads_touched: value('threads_touched'),
    constraints_added: value('constraints_added'),
  };
}

function getMergeChanges(report: unknown): MergeChange[] {
  if (!report || typeof report !== 'object') return [];
  const changes = (report as Record<string, unknown>).changes;
  if (!Array.isArray(changes)) return [];
  return changes.map((change) => {
    if (!change || typeof change !== 'object') return {};
    const record = change as Record<string, unknown>;
    const target =
      record.target && typeof record.target === 'object'
        ? (record.target as Record<string, unknown>)
        : null;
    return {
      category: typeof record.category === 'string' ? record.category : undefined,
      action: typeof record.action === 'string' ? record.action : undefined,
      summary: typeof record.summary === 'string' ? record.summary : undefined,
      target: target
        ? {
            id: typeof target.id === 'string' ? target.id : undefined,
            type: typeof target.type === 'string' ? target.type : undefined,
            field: typeof target.field === 'string' ? target.field : undefined,
          }
        : undefined,
    };
  });
}

function formatChangeLabel(change: MergeChange): string {
  const category = change.category ?? 'change';
  const action = change.action ?? 'update';
  return `${category}:${action}`;
}
